import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  acceptBuylistSubmission,
  approveBuylistSubmission,
  downloadBuylistLabel,
  ensureBuylistLabelQr,
  getBuylistSubmission,
  gradeBuylistItems,
  markBuylistOfferReady,
  markBuylistReceived,
  matchBuylistItem,
  quoteBuylistLabel,
  recordBuylistPayout,
  rejectBuylistSubmission,
  removeBuylistItem,
  replaceBuylistItem,
  setBuylistAmountPaid,
  splitBuylistItem,
  updateBuylistContact,
  ApproveBuylistPayload,
  BuylistChangeEvent,
  BuylistItemUpdate,
  BuylistSubmissionDetail,
  BuylistSubmissionItem,
  LabelQuoteResponse,
  LabelRateOption,
} from '../api/buylist';
import { Button } from '../components/base/Button';
import { BuylistCustomerFeedbackPanel } from '../components/buylist/BuylistCustomerFeedbackPanel';
import { Product, ProductSearchAutocomplete } from '../components/inventory/ProductSearchAutocomplete';
import { borderRadius, colorExtensions, colors, spacing, typography } from '../styles/tokens';
import {
  BUYLIST_CONTACT_SOURCES,
  BUYLIST_MARKET_LABELS,
  BUYLIST_REVIEW_FLAG_FILTERS,
  BUYLIST_REVIEW_LINE_SORTS,
  aggregateBuylistLotMarkets,
  aggregateBuylistReceiveTotals,
  buylistCopyProgress,
  buylistLineOfferTotal,
  buylistLineReviewFlags,
  buylistLotReviewFlags,
  filterAndSortBuylistReviewLines,
  lineExpectedQuantity,
  lineFullyReceived,
  lineReceivedQuantity,
  formatContactSource,
  formatMoneyAmount,
  formatOfferPercent,
  isActiveBuylistReceiveLine,
  mergeBuylistLiveMarketLines,
  offerPercentOfMarket,
  type BuylistReceiveTotalLine,
  type BuylistMarketKey,
  type BuylistReviewFlag,
  type BuylistReviewFlagFilter,
  type BuylistReviewLineSort,
} from '../utils/buylistOffer';
import { buylistCatalogLinePayload } from '../utils/buylistCatalog';
import { formatInstant } from '../utils/datetime';

const CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DMG'] as const;
const DEFAULT_FINISHES = ['normal', 'foil', 'etched'] as const;
const FINISH_BLOCK_HINT =
  'Finish needs manual pick — foil and etched both/neither have TCGPlayer SKUs.';

function finishesForLine(item: BuylistSubmissionItem): string[] {
  const available = item.availableFinishes;
  if (Array.isArray(available) && available.length > 0) {
    return available;
  }
  return [...DEFAULT_FINISHES];
}

/** Unique foil-family option (foil or etched only) — auto-select candidate. */
function uniqueFoilFamilyFinish(finishes: readonly string[]): string | null {
  const foilFamily = finishes.filter((f) => f === 'foil' || f === 'etched');
  return foilFamily.length === 1 ? foilFamily[0] : null;
}

function lineFinishBlocked(item: BuylistSubmissionItem): boolean {
  return Boolean(item.finishBlocked);
}

function lineNeedsFinishPick(item: BuylistSubmissionItem): boolean {
  return Boolean(item.finishNeedsManualPick) || lineFinishBlocked(item);
}

const MARKET_SIGNAL_KEYS: BuylistMarketKey[] = [
  'tcgLow',
  'tcgMarket',
  'manapoolMarket',
  'ckBuylist',
];

const WORKFLOW_STEPS = [
  { id: 'label', label: 'Buy prepaid label' },
  { id: 'offer', label: 'Send offer' },
  { id: 'pay', label: 'Mark paid (flexible)' },
  { id: 'receive', label: 'Mark received' },
  { id: 'review', label: 'Review & adjust cards' },
  { id: 'intake', label: 'Intake to inventory' },
] as const;

/** Submission statuses where match/remove/qty/condition edits are allowed (pre-intake). */
export const BUYLIST_ITEM_EDIT_STATUSES = [
  'submitted',
  'approved',
  'awaiting_shipment',
  'received',
  'grading',
  'offer_ready',
  'accepted',
] as const;

export function canEditBuylistSubmissionLines(
  status: string | null | undefined,
  hasPurchase: boolean,
): boolean {
  if (!status || hasPurchase) return false;
  return (BUYLIST_ITEM_EDIT_STATUSES as readonly string[]).includes(status);
}

type WorkflowStepId = (typeof WORKFLOW_STEPS)[number]['id'] | 'done';

const formatMoney = formatMoneyAmount;

/** Blank is unsaved, not $0. A typed 0 is a real amount. */
function parseAmountPaidInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

/** Visible kind labels. Rejected / unmatched / superseded are the product spellings. */
const CHANGE_KIND_LABEL: Record<string, string> = {
  condition: 'Condition',
  finish: 'Finish',
  quantity: 'Qty',
  received_qty: 'Received qty',
  rejected: 'Rejected',
  missing: 'Missing',
  unmatched: 'unmatched',
  superseded: 'superseded',
  match: 'Match',
  remove: 'Remove',
  split: 'Split',
  offer: 'Offer',
};

export function buylistChangeKindLabel(kind: string): string {
  return CHANGE_KIND_LABEL[kind] || kind;
}

/** Signed dollar delta. $0.00 is shown — condition/finish edits log it on purpose. */
export function formatBuylistOfferDelta(delta: number | null | undefined): string {
  if (delta == null || !Number.isFinite(Number(delta))) return '—';
  const value = Number(delta);
  const abs = formatMoney(Math.abs(value));
  if (value > 0) return `+${abs}`;
  if (value < 0) return `-${abs}`;
  return abs;
}

function changeEventCardLabel(event: BuylistChangeEvent): string {
  const number = (event.cardNumber || '').trim();
  return number ? `${event.cardName} #${number}` : event.cardName;
}

function lineOfferUnit(item: BuylistSubmissionItem): number | null {
  if (item.gradedUnitOffer != null && Number.isFinite(item.gradedUnitOffer)) {
    return item.gradedUnitOffer;
  }
  if (item.quotedUnitOffer != null && Number.isFinite(item.quotedUnitOffer)) {
    return item.quotedUnitOffer;
  }
  return null;
}

function lineOfferTotal(item: BuylistSubmissionItem): number | null {
  return buylistLineOfferTotal(item);
}

/**
 * Map a submission line into receive-total input.
 * `receivedQuantity` / `expectedQuantity` are optional until the partial-qty
 * payload lands; when present, the strip prorates offer by received units.
 */
function toReceiveTotalLine(item: BuylistSubmissionItem): BuylistReceiveTotalLine {
  const partial = item as BuylistSubmissionItem & {
    receivedQuantity?: number | null;
    expectedQuantity?: number | null;
  };
  return {
    status: item.status,
    included: item.included,
    gradedLineTotal: item.gradedLineTotal,
    quotedLineTotal: item.quotedLineTotal,
    gradedUnitOffer: item.gradedUnitOffer,
    quotedUnitOffer: item.quotedUnitOffer,
    quantity: item.quantity,
    lineCheckedAt: item.lineCheckedAt ?? null,
    receivedQuantity: partial.receivedQuantity,
    expectedQuantity: partial.expectedQuantity,
  };
}

/** What we will pay: unit offer × copies in hand (not the expected snapshot). */
function linePayableTotal(item: BuylistSubmissionItem): number | null {
  if (item.receivedQuantity == null && item.expectedQuantity == null) {
    return lineOfferTotal(item);
  }
  const unit = lineOfferUnit(item);
  if (unit == null) return lineOfferTotal(item);
  return unit * lineReceivedQuantity(item);
}

function lineReviewFlags(item: BuylistSubmissionItem, offerOverride?: number | null): BuylistReviewFlag[] {
  const skip =
    item.status === 'superseded' ||
    item.status === 'rejected_on_grading' ||
    item.status === 'unmatched' ||
    !item.included;
  const offer = offerOverride !== undefined ? offerOverride : lineOfferUnit(item);
  return buylistLineReviewFlags(
    offer,
    {
      tcgLow: item.tcgLow,
      tcgMarket: item.tcgMarket,
      manapoolMarket: item.manapoolMarket,
      ckBuylist: item.ckBuylist,
    },
    { hasCatalogId: Boolean(item.mtgjsonUuid), skip },
  );
}

const MarketSignalsCell: React.FC<{
  item: BuylistSubmissionItem;
  offerOverride?: number | null;
}> = ({ item, offerOverride }) => {
  const offer = offerOverride !== undefined ? offerOverride : lineOfferUnit(item);
  const flags = lineReviewFlags(item, offer);
  const highKeys = new Set(
    flags.filter((f) => f.kind === 'high_offer').map((f) => f.marketKey),
  );
  return (
    <div style={styles.marketStack}>
      {MARKET_SIGNAL_KEYS.map((key) => {
        const market = item[key];
        const hot = highKeys.has(key);
        const pct = offerPercentOfMarket(offer, market);
        return (
          <div key={key} style={styles.marketRow}>
            <span style={styles.marketLabel}>{BUYLIST_MARKET_LABELS[key]}</span>
            <span style={styles.marketValue}>{formatMoney(market)}</span>
            <span
              style={{
                ...styles.marketPct,
                ...(hot ? styles.marketPctHot : null),
              }}
              title={
                hot && pct != null
                  ? `Offer is ${Math.round(pct)}% of ${BUYLIST_MARKET_LABELS[key]}`
                  : undefined
              }
            >
              {formatOfferPercent(offer, market)}
            </span>
          </div>
        );
      })}
      {flags.length > 0 && (
        <div style={styles.flagStack}>
          {flags.map((flag) => (
            <div
              key={flag.kind === 'high_offer' ? `${flag.kind}-${flag.marketKey}` : flag.kind}
              style={flag.kind === 'missing_pricing' ? styles.flagMissing : styles.flagHigh}
            >
              {flag.label}
            </div>
          ))}
        </div>
      )}
      <div style={styles.marketRow}>
        <span style={styles.marketLabel}>Policy price</span>
        <span style={styles.marketValue} title={policyPriceTitle(item)}>
          {formatMoney(item.policyPrice)}
        </span>
        <span style={styles.marketPct} />
      </div>
    </div>
  );
};

function policyPriceTitle(item: BuylistSubmissionItem): string {
  const blend = item.policyPriceDebug?.blend;
  if (typeof blend === 'string' && blend) return `Live policy · ${blend}`;
  return 'Live policy price';
}

const LotPricingSummary: React.FC<{ items: BuylistSubmissionItem[] }> = ({ items }) => {
  const lot = aggregateBuylistLotMarkets(
    items.map((item) => ({
      quantity: lineReceivedQuantity(item),
      tcgLow: item.tcgLow,
      tcgMarket: item.tcgMarket,
      manapoolMarket: item.manapoolMarket,
      ckBuylist: item.ckBuylist,
      policyPrice: item.policyPrice,
      offerTotal: linePayableTotal(item),
    })),
  );
  const flags = buylistLotReviewFlags(lot);
  const highKeys = new Set(
    flags.filter((f) => f.kind === 'high_offer').map((f) => f.marketKey),
  );
  return (
    <section style={{ ...styles.card, marginBottom: spacing.md }}>
      <h3 style={{ ...typography.h3, marginTop: 0, marginBottom: spacing.xs }}>Lot markets</h3>
      <p style={styles.hint}>
        Sum of unit markets × copies in hand vs payable offer. Policy Price Total is the live
        soft-high quote × copies in hand (same quote as Add Inventory) and stays empty when that
        quote has no price. High-offer review flags offer &gt;90% of Mana Pool Market only; TCG /
        CK % stay for diagnosis (high MP % is often a high CK floor). CK Buylist is MTGJSON Card
        Kingdom data (same as Add Inventory).
      </p>
      <div style={styles.lotGrid}>
        {MARKET_SIGNAL_KEYS.map((key) => {
          const market = lot[key];
          const hot = highKeys.has(key);
          return (
            <div key={key} style={styles.lotCell}>
              <div style={styles.metaLabel}>{BUYLIST_MARKET_LABELS[key]}</div>
              <div style={styles.metaValue}>{formatMoney(market)}</div>
              <div style={{ ...styles.hintInline, ...(hot ? styles.marketPctHot : null) }}>
                offer {formatOfferPercent(lot.offerTotal, market)}
              </div>
            </div>
          );
        })}
        <div style={styles.lotCell}>
          <div style={styles.metaLabel}>Offer total</div>
          <div style={styles.metaValue}>{formatMoney(lot.offerTotal)}</div>
        </div>
        <div style={styles.lotCell}>
          <div style={styles.metaLabel}>Policy Price Total</div>
          <div style={styles.metaValue}>{formatMoney(lot.policyPrice)}</div>
        </div>
      </div>
      {flags.length > 0 && (
        <div style={{ ...styles.flagStack, marginTop: spacing.sm }}>
          {flags.map((flag) => (
            <div
              key={flag.kind === 'high_offer' ? `lot-${flag.kind}-${flag.marketKey}` : `lot-${flag.kind}`}
              style={flag.kind === 'missing_pricing' ? styles.flagMissing : styles.flagHigh}
            >
              Lot: {flag.label}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Live offer-dollar totals for the receive checklist.
 * Derived from the latest submission items, so Save / Reject / Missing /
 * Wrong card / Match / Remove / Split (mutation responses) and checklist
 * ticks refresh it. Header Market total and Quoted offer stay parse snapshots.
 */
export const ReceiveValueStrip: React.FC<{ items: BuylistSubmissionItem[] }> = ({ items }) => {
  const totals = aggregateBuylistReceiveTotals(items.map(toReceiveTotalLine));
  return (
    <section
      style={{ ...styles.card, marginBottom: spacing.md }}
      aria-live="polite"
      aria-label="Receive value"
    >
      <h3 style={{ ...typography.h3, marginTop: 0, marginBottom: spacing.xs }}>Receive value</h3>
      <p style={styles.hint}>
        Offer dollars we pay as cards are checked in. Graded line total wins over the quoted
        total. Excluded is the current offer on lines left out of the final offer (rejected,
        missing, unmatched, superseded, or not included) — not a history of dollars removed.
      </p>
      <div style={styles.lotGrid}>
        <div style={styles.lotCell}>
          <div style={styles.metaLabel}>Value received</div>
          <div style={styles.metaValue}>{formatMoney(totals.received)}</div>
        </div>
        <div style={styles.lotCell}>
          <div style={styles.metaLabel}>Still open</div>
          <div style={styles.metaValue}>{formatMoney(totals.stillOpen)}</div>
        </div>
        <div style={styles.lotCell}>
          <div style={styles.metaLabel}>Excluded (current value)</div>
          <div style={styles.metaValue}>{formatMoney(totals.excluded)}</div>
        </div>
      </div>
    </section>
  );
};

export type WorkflowOpts = {
  hasLabel: boolean;
  hasPurchase: boolean;
  /** payout_status === 'completed' (seller already paid — common before ship). */
  isPaidOut: boolean;
};

/**
 * Primary parcel/inventory step. Pay is tracked separately (isPaidOut) because
 * Eric often pays before sellers will ship — Mark paid must not lock the pipeline.
 */
export function deriveBuylistWorkflowStep(status: string, opts: WorkflowOpts): WorkflowStepId {
  if (status === 'paid') return 'done';
  if (status === 'rejected' || status === 'expired' || status === 'declined_by_customer') return 'done';

  // After intake: if not yet marked paid (pay-later), surface pay; else done.
  if (status === 'accepted' && opts.hasPurchase) {
    return opts.isPaidOut ? 'done' : 'pay';
  }
  if (status === 'accepted' && !opts.hasPurchase) return 'intake';

  if (status === 'received' || status === 'grading') return 'review';

  // offer_ready before receive = prepaid path waiting on package; after receive
  // review already advanced past offer email.
  if (status === 'offer_ready') {
    if (!opts.isPaidOut) return 'pay';
    return 'receive';
  }

  if (['submitted', 'approved'].includes(status) && !opts.hasLabel) return 'label';
  if (['submitted', 'approved', 'awaiting_shipment'].includes(status)) {
    // Prepaid: send offer → pay before package; pay-later can skip to receive.
    if (!opts.isPaidOut) return 'offer';
    return 'receive';
  }

  return 'label';
}

/** Whether a stepper row should show Done (pay is independent of parcel step). */
export function isWorkflowStepDone(
  stepId: (typeof WORKFLOW_STEPS)[number]['id'],
  current: WorkflowStepId,
  opts: WorkflowOpts,
): boolean {
  if (stepId === 'pay') return opts.isPaidOut;
  if (stepId === 'label') return opts.hasLabel || ['receive', 'review', 'intake', 'done'].includes(current) || (current === 'offer' && opts.hasLabel);
  if (stepId === 'offer') {
    return (
      opts.isPaidOut ||
      ['receive', 'review', 'intake', 'pay', 'done'].includes(current)
    );
  }
  const order = WORKFLOW_STEPS.map((s) => s.id);
  if (current === 'done') return true;
  const curIdx = order.indexOf(current as (typeof WORKFLOW_STEPS)[number]['id']);
  const stepIdx = order.indexOf(stepId);
  if (curIdx < 0 || stepIdx < 0) return false;
  return stepIdx < curIdx;
}

export const BuylistSubmissionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<BuylistSubmissionDetail | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [sellerName, setSellerName] = useState('');
  const [sellerStreet1, setSellerStreet1] = useState('');
  const [sellerStreet2, setSellerStreet2] = useState('');
  const [sellerCity, setSellerCity] = useState('');
  const [sellerState, setSellerState] = useState('');
  const [sellerZip, setSellerZip] = useState('');
  const [sellerPhone, setSellerPhone] = useState('');
  const [carrier, setCarrier] = useState<'usps' | 'ups' | 'fedex'>('usps');
  const [insuredValue, setInsuredValue] = useState('');
  const [weightOz, setWeightOz] = useState('');
  const [lengthIn, setLengthIn] = useState('');
  const [widthIn, setWidthIn] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [labelQuote, setLabelQuote] = useState<LabelQuoteResponse | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const [fetchingQr, setFetchingQr] = useState(false);

  const [amountPaidInput, setAmountPaidInput] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactSource, setContactSource] = useState('');
  const [rematchItemId, setRematchItemId] = useState<string | null>(null);
  const [matchItemId, setMatchItemId] = useState<string | null>(null);
  const [splitItemId, setSplitItemId] = useState<string | null>(null);
  const [cardNumberFilter, setCardNumberFilter] = useState('');
  const [uncheckedOnly, setUncheckedOnly] = useState(false);
  const [reviewFlagFilter, setReviewFlagFilter] = useState<BuylistReviewFlagFilter>('all');
  const [reviewLineSort, setReviewLineSort] = useState<BuylistReviewLineSort>('flag_severity');
  const cardNumberFilterRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBuylistSubmission(id);
      setDetail(data);
      if (data.sellerName) setSellerName(data.sellerName);
      if (data.sellerStreet1) setSellerStreet1(data.sellerStreet1);
      if (data.sellerStreet2) setSellerStreet2(data.sellerStreet2);
      if (data.sellerCity) setSellerCity(data.sellerCity);
      if (data.sellerState) setSellerState(data.sellerState);
      if (data.sellerZip) setSellerZip(data.sellerZip);
      if (data.sellerPhone) setSellerPhone(data.sellerPhone);
      if (data.labelCarrier === 'ups' || data.labelCarrier === 'usps' || data.labelCarrier === 'fedex') {
        setCarrier(data.labelCarrier);
      }
      const defaultIns = data.insuredValue ?? data.defaultInsuredValue ?? data.marketTotal;
      if (defaultIns != null) setInsuredValue(String(defaultIns));
      if (data.estimatedWeightOz != null) setWeightOz(String(data.estimatedWeightOz));
      // finalOffer is a suggestion only. Prefilling this field made an unsaved
      // null amount_paid look like the offer had already been recorded.
      setAmountPaidInput(data.amountPaid != null ? String(data.amountPaid) : '');
      setContactEmail(data.customerEmail || '');
      setContactName(data.customerName || '');
      setContactSource(data.contactSource || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const applySubmissionDetail = (next: BuylistSubmissionDetail) => {
    setDetail((current) => {
      if (!current) return next;
      return {
        ...next,
        items: mergeBuylistLiveMarketLines(current.items, next.items ?? []),
      };
    });
  };

  const run = async (action: () => Promise<unknown>, okMessage = 'Updated.') => {
    // Guard double-submit (e.g. concurrent Accept racing ensure_variant).
    if (actionPending) return;
    setActionPending(true);
    setMessage(null);
    setError(null);
    try {
      const result = await action();
      // Prefer the mutation response (often scoped markets) over a full GET reload.
      if (
        result &&
        typeof result === 'object' &&
        Array.isArray((result as BuylistSubmissionDetail).items) &&
        typeof (result as BuylistSubmissionDetail).id === 'string'
      ) {
        applySubmissionDetail(result as BuylistSubmissionDetail);
      } else {
        await load();
      }
      setMessage(okMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionPending(false);
    }
  };

  const sellerPayload = (): ApproveBuylistPayload => ({
    seller: {
      name: sellerName.trim(),
      street1: sellerStreet1.trim(),
      street2: sellerStreet2.trim() || undefined,
      city: sellerCity.trim(),
      state: sellerState.trim().toUpperCase(),
      zip: sellerZip.trim(),
      country: 'US',
      phone: sellerPhone.trim() || undefined,
    },
    label_carrier: carrier,
    insured_value: insuredValue ? Number(insuredValue) : undefined,
    weight_oz: weightOz ? Number(weightOz) : undefined,
    length_in: lengthIn ? Number(lengthIn) : undefined,
    width_in: widthIn ? Number(widthIn) : undefined,
    height_in: heightIn ? Number(heightIn) : undefined,
  });

  const sellerReady =
    Boolean(sellerName.trim()) &&
    Boolean(sellerStreet1.trim()) &&
    Boolean(sellerCity.trim()) &&
    Boolean(sellerState.trim()) &&
    Boolean(sellerZip.trim());

  const parcelDimsReady =
    Number(lengthIn) > 0 && Number(widthIn) > 0 && Number(heightIn) > 0;
  const parcelWeightReady = Number(weightOz) > 0;
  const parcelReady = parcelDimsReady && parcelWeightReady;

  const selectedOption: LabelRateOption | null =
    labelQuote?.options?.find((opt) => opt.rateId === selectedRateId) ||
    labelQuote?.options?.[0] ||
    null;

  const getQuote = async () => {
    if (!id) return;
    setQuoting(true);
    setMessage(null);
    setError(null);
    setLabelQuote(null);
    setSelectedRateId(null);
    try {
      const quoted = await quoteBuylistLabel(id, sellerPayload());
      setLabelQuote(quoted);
      setDetail(quoted.submission);
      const first = quoted.options?.[0];
      if (first) {
        setSelectedRateId(first.rateId);
        const carrierKey = first.carrier.toLowerCase().includes('fedex')
          ? 'fedex'
          : first.carrier.toLowerCase().includes('ups')
            ? 'ups'
            : 'usps';
        setCarrier(carrierKey);
      }
      const n = quoted.options?.length || 0;
      setMessage(
        n > 0
          ? `Label quote: ${n} service option${n === 1 ? '' : 's'} (postage + insurance fee). Pick one before buying.`
          : 'Quote returned no service options.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quote failed');
    } finally {
      setQuoting(false);
    }
  };

  const confirmBuy = async () => {
    if (!id || !selectedOption) return;
    setApproving(true);
    setMessage(null);
    setError(null);
    try {
      const carrierKey = selectedOption.carrier.toLowerCase().includes('fedex')
        ? 'fedex'
        : selectedOption.carrier.toLowerCase().includes('ups')
          ? 'ups'
          : 'usps';
      const bought = await approveBuylistSubmission(id, {
        ...sellerPayload(),
        label_carrier: carrierKey,
        easypost_shipment_id: selectedOption.shipmentId,
        easypost_rate_id: selectedOption.rateId,
        confirm_rate_dollars: selectedOption.rateDollars,
        confirm_total_dollars: selectedOption.totalDollars,
      });
      setLabelQuote(null);
      setSelectedRateId(null);
      await load();
      const emailed = (bought as BuylistSubmissionDetail & { labelEmailSent?: boolean }).labelEmailSent;
      setMessage(
        emailed
          ? 'Approved — prepaid label purchased; seller email attempted.'
          : 'Approved — prepaid label purchased. Auto-email did not send (or no seller email) — download the PDF / QR below and send manually.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const downloadLabelPdf = async () => {
    if (!id) return;
    setDownloadingLabel(true);
    setError(null);
    try {
      await downloadBuylistLabel(id, detail?.referenceCode || id);
      setMessage('Label file downloaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Label download failed');
    } finally {
      setDownloadingLabel(false);
    }
  };

  const fetchLabelQr = async () => {
    if (!id) return;
    setFetchingQr(true);
    setError(null);
    try {
      const result = await ensureBuylistLabelQr(id);
      setDetail(result.submission);
      setMessage(result.created ? 'Label Broker QR created.' : 'Label Broker QR already on file.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'QR unavailable for this label');
    } finally {
      setFetchingQr(false);
    }
  };

  const parsedAmountPaid = parseAmountPaidInput(amountPaidInput);

  if (!id) return null;

  const hasLabel = Boolean(detail?.labelUrl);
  const hasPurchase = Boolean(detail?.purchaseId);
  const isPaidOut = detail?.payoutStatus === 'completed' || detail?.status === 'paid';
  const workflowOpts = { hasLabel, hasPurchase, isPaidOut };
  const effectiveStep: WorkflowStepId = detail
    ? deriveBuylistWorkflowStep(detail.status, workflowOpts)
    : 'label';

  const lines = detail?.items.filter((item) => isActiveBuylistReceiveLine(item)) ?? [];
  const rejected =
    detail?.items.filter(
      (i) =>
        i.status === 'rejected_on_grading' ||
        i.status === 'unmatched' ||
        i.status === 'superseded',
    ) ?? [];

  const filteredLines = (() => {
    const needle = cardNumberFilter.trim().toLowerCase();
    const textFiltered = lines.filter((item) => {
      if (uncheckedOnly && lineFullyReceived(item)) return false;
      if (!needle) return true;
      const num = (item.cardNumber || '').toLowerCase();
      const name = (item.cardName || '').toLowerCase();
      return num.includes(needle) || name.includes(needle);
    });
    return filterAndSortBuylistReviewLines(textFiltered, {
      flagFilter: reviewFlagFilter,
      sort: reviewLineSort,
      getFlags: (item) => lineReviewFlags(item),
      getName: (item) => item.cardName,
      getOfferUnit: (item) => lineOfferUnit(item),
      getManapoolMarket: (item) => item.manapoolMarket ?? null,
    });
  })();

  const copyProgress = buylistCopyProgress(lines);
  const reviewFlaggedCount = lines.filter((i) => lineReviewFlags(i).length > 0).length;
  const missingPricingCount = lines.filter((i) =>
    lineReviewFlags(i).some((f) => f.kind === 'missing_pricing'),
  ).length;
  const highOfferCount = lines.filter((i) =>
    lineReviewFlags(i).some((f) => f.kind === 'high_offer'),
  ).length;
  const canApprove = detail && ['submitted', 'approved'].includes(detail.status);
  const canReceive =
    detail && ['submitted', 'awaiting_shipment', 'approved', 'offer_ready'].includes(detail.status);
  const canEditLines = canEditBuylistSubmissionLines(detail?.status, hasPurchase);
  const canSendOffer =
    detail &&
    ['submitted', 'approved', 'awaiting_shipment', 'received', 'grading', 'offer_ready'].includes(
      detail.status,
    );
  const canIntake = detail && ['received', 'grading', 'offer_ready', 'accepted'].includes(detail.status);
  const finishBlockedLines = (detail?.items ?? []).filter(
    (item) => item.included !== false && lineFinishBlocked(item) && lineReceivedQuantity(item) > 0,
  );
  const intakeBlockedByFinish = finishBlockedLines.length > 0;
  const finishBlockSummary = intakeBlockedByFinish
    ? finishBlockedLines[0]?.finishBlockMessage || FINISH_BLOCK_HINT
    : null;
  // Flexible: Mark paid whenever payout is not done (prepaid before ship is normal).
  const canMarkPaid =
    Boolean(detail) &&
    !isPaidOut &&
    !['rejected', 'expired', 'declined_by_customer'].includes(detail!.status);

  const markPaid = () =>
    void run(
      () => recordBuylistPayout(id, 'completed', undefined, parsedAmountPaid),
      isPaidOut
        ? 'Already paid.'
        : 'Seller marked paid. Parcel steps (receive → review → intake) still available.',
    );

  const payoutText =
    detail?.payoutDetails == null
      ? null
      : typeof detail.payoutDetails === 'object'
        ? Object.entries(detail.payoutDetails)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ')
        : String(detail.payoutDetails);

  const readiness = detail?.labelReadiness;

  const saveItemUpdates = (updates: BuylistItemUpdate[], ok = 'Line updated.') =>
    void run(() => gradeBuylistItems(id, updates), ok);

  /**
   * Receive tally is hot-path: optimistic N-of-M + PATCH without a full GET reload.
   * Checklist responses omit live markets; merge those stamps back onto the server row.
   */
  const applyReceiveTally = (
    itemId: string,
    patch: Omit<BuylistItemUpdate, 'item_id'>,
    optimistic: (item: BuylistSubmissionItem) => BuylistSubmissionItem,
    ok: string,
  ) => {
    if (!id || !detail) return;
    const previous = detail;
    setDetail({
      ...detail,
      items: detail.items.map((item) => (item.id === itemId ? optimistic(item) : item)),
    });
    setError(null);
    void gradeBuylistItems(id, [{ ...patch, item_id: itemId }])
      .then((updated) => {
        setDetail((current) => {
          if (!current) return updated;
          return {
            ...updated,
            items: mergeBuylistLiveMarketLines(current.items, updated.items ?? []),
          };
        });
        setMessage(ok);
      })
      .catch(async (err) => {
        setDetail(previous);
        setError(err instanceof Error ? err.message : 'Checklist update failed');
        try {
          await load();
        } catch {
          /* keep previous / error */
        }
      });
  };

  const stepReceived = (item: BuylistSubmissionItem, delta: number): BuylistSubmissionItem => {
    const expected = lineExpectedQuantity(item);
    const received = Math.max(0, Math.min(expected, lineReceivedQuantity(item) + delta));
    const complete = expected > 0 && received >= expected;
    return {
      ...item,
      receivedQuantity: received,
      quantity: received,
      lineCheckedAt: complete ? item.lineCheckedAt || new Date().toISOString() : null,
    };
  };

  const toggleLineChecked = (itemId: string, checked: boolean) => {
    const item = detail?.items.find((row) => row.id === itemId);
    if (!item) return;
    const next = stepReceived(item, checked ? 1 : -1);
    const received = lineReceivedQuantity(next);
    const expected = lineExpectedQuantity(next);
    applyReceiveTally(
      itemId,
      { line_checked: checked },
      () => next,
      `${received} of ${expected}`,
    );
  };

  const adjustReceived = (itemId: string, delta: number) => {
    const item = detail?.items.find((row) => row.id === itemId);
    if (!item) return;
    const next = stepReceived(item, delta);
    applyReceiveTally(
      itemId,
      { received_delta: delta },
      () => next,
      `${lineReceivedQuantity(next)} of ${lineExpectedQuantity(next)}`,
    );
  };

  const correctExpected = (itemId: string, expected: number) => {
    if (!id || !Number.isFinite(expected) || expected < 0) return;
    void run(
      () => gradeBuylistItems(id, [{ item_id: itemId, expected_quantity: expected }]),
      `Expected corrected to ${expected}.`,
    );
  };

  const approveAllMatched = () =>
    void run(
      () =>
        gradeBuylistItems(
          id,
          (detail?.items ?? [])
            .filter((i) => i.included && i.status === 'matched')
            .map((i) => ({ item_id: i.id, status: 'accepted_on_grading' })),
        ),
      'Matched lines approved for the offer (no inventory yet).',
    );

  return (
    <div style={styles.page}>
      <Link to="/buylist" style={styles.backLink}>
        ← Back to queue
      </Link>
      <h1 style={{ ...typography.h1, marginTop: spacing.md, marginBottom: spacing.xs }}>
        {detail?.referenceCode || 'Buylist submission'}
      </h1>
      {loading && <p style={styles.hint}>Loading…</p>}
      {error && <p style={styles.error}>{error}</p>}
      {message && <p style={styles.success}>{message}</p>}

      {detail && (
        <>
          <div style={styles.metaGrid}>
            <Meta label="Status" value={detail.status.replace(/_/g, ' ')} />
            <Meta label="Customer" value={detail.customerName || detail.customerEmail || '—'} />
            <Meta label="Contact source" value={formatContactSource(detail.contactSource)} />
            <Meta label="Market total" value={formatMoney(detail.marketTotal)} />
            <Meta
              label="Quoted offer"
              value={`${formatMoney(detail.quotedOfferMin)} – ${formatMoney(detail.quotedOfferMax)}`}
            />
            <Meta label="Final offer" value={formatMoney(detail.finalOffer)} />
            <Meta label="Amount paid" value={formatMoney(detail.amountPaid)} />
            <Meta
              label="Payout"
              value={`${detail.payoutMethod || '—'} (${detail.payoutStatus})${
                isPaidOut && detail.status !== 'paid' ? ' · paid (pipeline open)' : ''
              }`}
            />
            {payoutText && <Meta label="Payout details" value={payoutText} />}
            {detail.shippingTracking && (
              <Meta
                label="Tracking"
                value={`${detail.shippingTracking}${
                  detail.labelCarrier
                    ? ` · ${detail.labelCarrier.toUpperCase()} ${detail.labelService || ''}`
                    : ''
                }${detail.labelPurchasedAt ? ` · ${formatInstant(detail.labelPurchasedAt)}` : ''}`}
              />
            )}
          </div>

          {canEditLines && (
            <section style={{ ...styles.card, marginBottom: spacing.lg }}>
              <h2 style={{ ...typography.h2, marginTop: 0 }}>Seller contact</h2>
              <p style={styles.hint}>
                Add or fix email and where the contact came from any time before intake.
              </p>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Email</span>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    style={styles.input}
                    placeholder="seller@example.com"
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Name</span>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    style={styles.input}
                    placeholder="Optional"
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Contact came from</span>
                  <select
                    value={contactSource}
                    onChange={(e) => setContactSource(e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Unset</option>
                    {BUYLIST_CONTACT_SOURCES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ ...styles.actions, marginTop: spacing.sm }}>
                <Button
                  type="button"
                  variant="outline"
                  disabled={actionPending}
                  onClick={() =>
                    void run(
                      () =>
                        updateBuylistContact(id, {
                          email: contactEmail.trim() || undefined,
                          // Omit empty name so Save contact does not wipe an existing name
                          // when only email / source is being set.
                          ...(contactName.trim() ? { name: contactName.trim() } : {}),
                          contact_source: contactSource || undefined,
                          clear_contact_source: !contactSource,
                        }),
                      'Seller contact saved.',
                    )
                  }
                >
                  Save contact
                </Button>
              </div>
            </section>
          )}

          <section style={styles.card}>
            <h2 style={{ ...typography.h2, marginTop: 0 }}>Workflow</h2>
            <p style={styles.hint}>
              <strong>Prepaid (usual):</strong> Send offer → Mark paid → wait for package → Mark
              received → review/edit lines → Intake.
              <br />
              <strong>Pay later:</strong> receive → review → offer → intake → Mark paid.
              <br />
              Mark paid is flexible — pay whenever the seller is paid (often before cards ship). It
              does not require cards to have arrived and does not block receive/intake.
            </p>
            <ol style={styles.stepList}>
              {WORKFLOW_STEPS.map((s) => {
                const isDone = isWorkflowStepDone(s.id, effectiveStep, workflowOpts);
                const isCurrent = s.id === effectiveStep;
                return (
                  <li
                    key={s.id}
                    style={{
                      ...styles.stepItem,
                      ...(isCurrent ? styles.stepCurrent : null),
                      ...(isDone && !isCurrent ? styles.stepDone : null),
                    }}
                  >
                    <span style={styles.stepNum}>{WORKFLOW_STEPS.findIndex((x) => x.id === s.id) + 1}</span>
                    <span>{s.label}</span>
                    {s.id === 'pay' && (
                      <span style={styles.stepBadgeMuted}>anytime</span>
                    )}
                    {isCurrent && <span style={styles.stepBadge}>Current</span>}
                    {isDone && !isCurrent && <span style={styles.stepBadgeMuted}>Done</span>}
                  </li>
                );
              })}
            </ol>

            <div style={{ ...styles.actions, marginTop: spacing.md }}>
              {effectiveStep === 'label' && (
                <>
                  {canSendOffer && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        void run(() => markBuylistOfferReady(id), 'Offer emailed to customer.')
                      }
                    >
                      Send offer to customer
                    </Button>
                  )}
                  {canMarkPaid && (
                    <Button type="button" variant="secondary" onClick={markPaid}>
                      Mark paid
                    </Button>
                  )}
                  {canReceive && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        void run(
                          () => markBuylistReceived(id, parsedAmountPaid),
                          'Marked received (no prepaid label) — review cards next.',
                        )
                      }
                    >
                      Skip label · Mark received
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'offer' && (
                <>
                  {canSendOffer && (
                    <Button
                      type="button"
                      onClick={() =>
                        void run(() => markBuylistOfferReady(id), 'Offer emailed to customer.')
                      }
                    >
                      Send offer to customer
                    </Button>
                  )}
                  {canMarkPaid && (
                    <Button type="button" variant="secondary" onClick={markPaid}>
                      Mark paid
                    </Button>
                  )}
                  {canReceive && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        void run(
                          () => markBuylistReceived(id, parsedAmountPaid),
                          'Marked received — review cards next.',
                        )
                      }
                    >
                      Skip to Mark received
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'pay' && canMarkPaid && (
                <>
                  <Button type="button" onClick={markPaid}>
                    Mark paid
                  </Button>
                  {canReceive && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        void run(
                          () => markBuylistReceived(id, parsedAmountPaid),
                          'Marked received — review cards next.',
                        )
                      }
                    >
                      Mark received
                    </Button>
                  )}
                  {canIntake && (
                    <Button
                      type="button"
                      variant="outline"
                      loading={actionPending}
                      disabled={intakeBlockedByFinish}
                      title={finishBlockSummary ?? undefined}
                      onClick={() =>
                        void run(
                          () => acceptBuylistSubmission(id),
                          'Intake created — copies in stock.',
                        )
                      }
                    >
                      Intake to inventory
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'receive' && canReceive && (
                <>
                  <Button
                    type="button"
                    onClick={() =>
                      void run(
                        () => markBuylistReceived(id, parsedAmountPaid),
                        'Marked received — review cards next.',
                      )
                    }
                  >
                    Mark received
                  </Button>
                  {canMarkPaid && (
                    <Button type="button" variant="secondary" onClick={markPaid}>
                      Mark paid
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'review' && (
                <>
                  <Button type="button" variant="outline" onClick={approveAllMatched}>
                    Approve all matched lines
                  </Button>
                  {canSendOffer && (
                    <Button
                      type="button"
                      onClick={() =>
                        void run(
                          () => markBuylistOfferReady(id),
                          'Offer emailed to customer.',
                        )
                      }
                    >
                      Send offer to customer
                    </Button>
                  )}
                  {canIntake && (
                    <Button
                      type="button"
                      variant="secondary"
                      loading={actionPending}
                      disabled={intakeBlockedByFinish}
                      title={finishBlockSummary ?? undefined}
                      onClick={() =>
                        void run(
                          () => acceptBuylistSubmission(id),
                          'Intake created — copies in stock.',
                        )
                      }
                    >
                      Intake to inventory
                    </Button>
                  )}
                  {canMarkPaid && (
                    <Button type="button" variant="outline" onClick={markPaid}>
                      Mark paid
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'intake' && (
                <>
                  {canSendOffer && detail.status === 'offer_ready' && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void run(() => markBuylistOfferReady(id), 'Offer re-sent.')}
                    >
                      Re-send offer
                    </Button>
                  )}
                  {canIntake && (
                    <Button
                      type="button"
                      loading={actionPending}
                      disabled={intakeBlockedByFinish}
                      title={finishBlockSummary ?? undefined}
                      onClick={() =>
                        void run(
                          () => acceptBuylistSubmission(id),
                          'Intake created — copies in stock.',
                        )
                      }
                    >
                      Intake to inventory
                    </Button>
                  )}
                  {canMarkPaid && (
                    <Button type="button" variant="secondary" onClick={markPaid}>
                      Mark paid
                    </Button>
                  )}
                </>
              )}
              {effectiveStep === 'done' && isPaidOut && (
                <p style={styles.hint}>Paid and closed (or pipeline complete).</p>
              )}
            </div>

            <p style={{ ...styles.hint, marginTop: spacing.sm }}>
              <strong>Approve all matched lines</strong> only flips line status for the offer — no
              inventory.{' '}
              <strong>Intake to inventory</strong> creates the purchase and in-stock copies
              (COGS from amount paid). <strong>Mark paid</strong> records that the seller was paid —
              safe before or after the package arrives.
            </p>

            {finishBlockSummary ? (
              <p role="status" style={styles.warn}>
                Intake blocked: {finishBlockSummary}
                {finishBlockedLines.length > 1
                  ? ` (${finishBlockedLines.length} lines)`
                  : finishBlockedLines[0]
                    ? ` — ${finishBlockedLines[0].cardName}`
                    : ''}
              </p>
            ) : null}

            <button
              type="button"
              style={styles.advancedToggle}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide' : 'Show'} other actions
            </button>
            {showAdvanced && (
              <div style={{ ...styles.actions, marginTop: spacing.sm }}>
                {canReceive && effectiveStep !== 'receive' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void run(() => markBuylistReceived(id, parsedAmountPaid))}
                  >
                    Mark received
                  </Button>
                )}
                {canSendOffer && effectiveStep !== 'offer' && effectiveStep !== 'review' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void run(() => markBuylistOfferReady(id))}
                  >
                    Send offer to customer
                  </Button>
                )}
                {canIntake && effectiveStep !== 'intake' && effectiveStep !== 'review' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    loading={actionPending}
                    disabled={intakeBlockedByFinish}
                    title={finishBlockSummary ?? undefined}
                    onClick={() => void run(() => acceptBuylistSubmission(id), 'Intake created.')}
                  >
                    Intake to inventory
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={approveAllMatched}>
                  Approve all matched lines
                </Button>
                <div style={{ ...styles.inlineRow, width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Rejection reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => void run(() => rejectBuylistSubmission(id, rejectReason || 'Rejected'))}
                  >
                    Reject submission
                  </Button>
                </div>
              </div>
            )}
          </section>

          <BuylistCustomerFeedbackPanel
            text={detail.customerFeedbackText || ''}
            onCopied={(msg) => {
              setError(null);
              setMessage(msg);
            }}
          />

          <section style={styles.card}>
            <h2 style={{ ...typography.h2, marginTop: 0 }}>Amount paid → acquisition cost</h2>
            <p style={styles.hint}>
              Enter the real total paid to the seller. At Intake to inventory, card costs scale so
              copy COGS sums to this amount. Often set when you Mark paid (including before the
              package ships); adjust after receive if cards differ.
            </p>
            {detail.amountPaid == null && (
              <p style={styles.warn}>
                Not saved
                {detail.finalOffer != null
                  ? ` — final offer is ${formatMoney(detail.finalOffer)}. That number is not the amount paid until you save it.`
                  : ' — enter what you paid the seller, then save.'}
              </p>
            )}
            <div style={styles.inlineRow}>
              <label style={{ ...styles.field, flex: '0 0 180px' }}>
                <span style={styles.label}>Amount paid ($)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={amountPaidInput}
                  placeholder={detail.amountPaid == null ? 'Not saved' : undefined}
                  onChange={(e) => setAmountPaidInput(e.target.value)}
                  style={styles.input}
                />
              </label>
              {detail.amountPaid == null && detail.finalOffer != null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAmountPaidInput(String(detail.finalOffer))}
                >
                  Use final offer
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                disabled={parsedAmountPaid === undefined}
                onClick={() =>
                  void run(
                    () => setBuylistAmountPaid(id, parsedAmountPaid!),
                    'Amount paid saved.',
                  )
                }
              >
                Save amount paid
              </Button>
            </div>
          </section>

          {canApprove && effectiveStep === 'label' && (
            <section style={styles.card}>
              <h2 style={{ ...typography.h2, marginTop: 0 }}>1 · Buy prepaid label</h2>
              <p style={styles.hint}>
                Origin = seller address. USPS ships to buylist ship-to in Settings (PO Box OK); UPS
                ships to account address. Enter box L×W×H (inches) and weight before quoting — rates
                without dimensions are wrong. Quote first; label is not purchased until you confirm.
              </p>

              {readiness && !readiness.destinationComplete && (
                <p style={styles.warn}>
                  Destination incomplete — set ship-to under Settings → Shipping before quoting.
                </p>
              )}
              {readiness?.missing?.length ? (
                <p style={styles.hint}>Missing for label: {readiness.missing.join(', ')}</p>
              ) : null}

              <div style={styles.formGrid}>
                <label style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <span style={styles.label}>Seller name</span>
                  <input value={sellerName} onChange={(e) => setSellerName(e.target.value)} style={styles.input} />
                </label>
                <label style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <span style={styles.label}>Street 1</span>
                  <input
                    value={sellerStreet1}
                    onChange={(e) => setSellerStreet1(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <label style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <span style={styles.label}>Street 2</span>
                  <input
                    value={sellerStreet2}
                    onChange={(e) => setSellerStreet2(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>City</span>
                  <input value={sellerCity} onChange={(e) => setSellerCity(e.target.value)} style={styles.input} />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>State</span>
                  <input
                    value={sellerState}
                    onChange={(e) => setSellerState(e.target.value.toUpperCase())}
                    maxLength={2}
                    style={styles.input}
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>ZIP</span>
                  <input value={sellerZip} onChange={(e) => setSellerZip(e.target.value)} style={styles.input} />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Phone (recommended)</span>
                  <input value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} style={styles.input} />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Insured value ($)</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={insuredValue}
                    onChange={(e) => {
                      setInsuredValue(e.target.value);
                      setLabelQuote(null);
                      setSelectedRateId(null);
                    }}
                    style={styles.input}
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Parcel weight (oz)</span>
                  <input
                    type="number"
                    min={0.1}
                    step="0.01"
                    value={weightOz}
                    onChange={(e) => {
                      setWeightOz(e.target.value);
                      setLabelQuote(null);
                      setSelectedRateId(null);
                    }}
                    style={styles.input}
                    required
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Length (in)</span>
                  <input
                    type="number"
                    min={0.1}
                    step="0.1"
                    value={lengthIn}
                    onChange={(e) => {
                      setLengthIn(e.target.value);
                      setLabelQuote(null);
                      setSelectedRateId(null);
                    }}
                    style={styles.input}
                    placeholder="L"
                    required
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Width (in)</span>
                  <input
                    type="number"
                    min={0.1}
                    step="0.1"
                    value={widthIn}
                    onChange={(e) => {
                      setWidthIn(e.target.value);
                      setLabelQuote(null);
                      setSelectedRateId(null);
                    }}
                    style={styles.input}
                    placeholder="W"
                    required
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Height (in)</span>
                  <input
                    type="number"
                    min={0.1}
                    step="0.1"
                    value={heightIn}
                    onChange={(e) => {
                      setHeightIn(e.target.value);
                      setLabelQuote(null);
                      setSelectedRateId(null);
                    }}
                    style={styles.input}
                    placeholder="H"
                    required
                  />
                </label>
              </div>

              <p style={styles.hint}>
                Get label price returns Ground for USPS / UPS / FedEx (cheapest ground per carrier,
                including EasyPost UPSDAP accounts) plus 3-day options when returned. Totals include
                insurance premium (~1% of insured value, $1 minimum) — not just postage. Irrelevant
                international carrier warnings are hidden.
              </p>

              {!parcelReady && (
                <p style={styles.warn}>
                  Enter box dimensions (L×W×H inches) and weight before getting a label price.
                </p>
              )}

              <div style={{ ...styles.inlineRow, marginTop: spacing.md }}>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!sellerReady || !parcelReady || quoting}
                  loading={quoting}
                  onClick={() => void getQuote()}
                >
                  Get label price
                </Button>
              </div>

              {labelQuote && (
                <div style={{ ...styles.quoteBox, marginTop: spacing.md, minWidth: 0, width: '100%' }}>
                  <strong>
                    Service options
                    {labelQuote.insuranceFeeDollars > 0
                      ? ` · insurance fee ${formatMoney(labelQuote.insuranceFeeDollars)} on ${formatMoney(labelQuote.insuranceDollars)} declared`
                      : ' · no insurance'}
                  </strong>
                  <span style={styles.hint}>
                    {labelQuote.weightOz} oz · {labelQuote.lengthIn}×{labelQuote.widthIn}×
                    {labelQuote.heightIn} in
                  </span>

                  {(labelQuote.options || []).length === 0 ? (
                    <p style={styles.warn}>
                      No carrier rates returned. Check EasyPost carrier accounts and ship-to
                      addresses in Settings → Shipping.
                    </p>
                  ) : (
                    <div style={styles.rateOptionList} role="radiogroup" aria-label="Shipping service">
                      {(['ground', 'three_day'] as const).map((klass) => {
                        const rows = (labelQuote.options || []).filter(
                          (opt) => opt.serviceClass === klass,
                        );
                        if (rows.length === 0) return null;
                        return (
                          <div key={klass} style={styles.rateOptionGroup}>
                            <div style={styles.rateOptionGroupTitle}>
                              {klass === 'ground' ? 'Ground' : '3-day'}
                            </div>
                            {rows.map((opt) => {
                              const checked = selectedOption?.rateId === opt.rateId;
                              return (
                                <label
                                  key={opt.rateId}
                                  style={{
                                    ...styles.rateOptionRow,
                                    ...(checked ? styles.rateOptionRowSelected : null),
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="buylist-label-rate"
                                    checked={checked}
                                    onChange={() => {
                                      setSelectedRateId(opt.rateId);
                                      const carrierKey = opt.carrier.toLowerCase().includes('fedex')
                                        ? 'fedex'
                                        : opt.carrier.toLowerCase().includes('ups')
                                          ? 'ups'
                                          : 'usps';
                                      setCarrier(carrierKey);
                                    }}
                                  />
                                  <span style={styles.rateOptionMain}>
                                    <span>{opt.displayName || `${opt.carrier} ${opt.service}`}</span>
                                    <span style={styles.hint}>
                                      Postage {formatMoney(opt.rateDollars)}
                                      {opt.insuranceFeeDollars > 0
                                        ? ` + insurance ${formatMoney(opt.insuranceFeeDollars)}`
                                        : ''}
                                    </span>
                                  </span>
                                  <strong style={styles.rateOptionTotal}>
                                    {formatMoney(opt.totalDollars)}
                                  </strong>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(labelQuote.messages || []).length > 0 && (
                    <ul style={styles.rateMessages}>
                      {(labelQuote.messages || []).map((msg) => (
                        <li key={msg} style={styles.hint}>
                          {msg}
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    type="button"
                    disabled={approving || !selectedOption}
                    loading={approving}
                    onClick={() => void confirmBuy()}
                  >
                    Confirm &amp; buy{' '}
                    {selectedOption
                      ? `${selectedOption.displayName} (${formatMoney(selectedOption.totalDollars)})`
                      : 'label'}
                  </Button>
                </div>
              )}
            </section>
          )}

          {detail.labelUrl && (
            <section style={{ ...styles.card, marginBottom: spacing.lg }}>
              <h2 style={{ ...typography.h2, marginTop: 0 }}>Prepaid label — send to customer</h2>
              <p style={styles.hint}>
                Auto-email (<code>buylist_label_ready</code>) only goes out when SMTP is configured and a
                seller email exists. Until then, download the PDF and/or show the USPS Label Broker QR
                and send them yourself (text, email, Discord, etc.).
              </p>
              <div style={{ ...styles.inlineRow, flexWrap: 'wrap', gap: spacing.sm }}>
                <Button
                  type="button"
                  variant="outline"
                  loading={downloadingLabel}
                  disabled={downloadingLabel}
                  onClick={() => void downloadLabelPdf()}
                >
                  Download label PDF
                </Button>
                <a
                  href={detail.labelUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.link}
                >
                  Open EasyPost label URL
                </a>
                {detail.shippingTracking && (
                  <span style={styles.hint}>Tracking: {detail.shippingTracking}</span>
                )}
              </div>
              {detail.labelQrUrl ? (
                <div style={{ marginTop: spacing.md }}>
                  <p style={{ ...styles.label, marginBottom: spacing.xs }}>
                    USPS Label Broker QR — customer shows this at a participating Post Office
                  </p>
                  <img
                    src={detail.labelQrUrl}
                    alt="USPS Label Broker QR code"
                    style={styles.labelQrImg}
                  />
                  <div style={{ marginTop: spacing.sm }}>
                    <a href={detail.labelQrUrl} target="_blank" rel="noreferrer" style={styles.link}>
                      Open QR image
                    </a>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: spacing.md }}>
                  <p style={styles.hint}>
                    No Label Broker QR stored yet (USPS only). UPS labels are PDF-print; QR is not
                    available from EasyPost for most non-USPS carriers.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    loading={fetchingQr}
                    disabled={fetchingQr || !detail.easypostShipmentId}
                    onClick={() => void fetchLabelQr()}
                  >
                    Request USPS Label Broker QR
                  </Button>
                </div>
              )}
            </section>
          )}

          <h2 style={{ ...typography.h2, marginTop: spacing.lg }}>Receive checklist</h2>
          {canEditLines && (
            <p style={styles.hint}>
              Edit matches and line details any time before intake (from parse / pending through
              receive). Filter by collector number while sorting the package. Mark copies as you
              find them — 1 of 3, then 2 of 3 — without changing how many were expected. Offer,
              COGS, and intake follow copies in hand. Correct expected only with the Expected
              control (it is logged). Wrong card states how many copies it applies to and
              supersedes that line. Expected ≥ 2: Split peels the declared count into sibling
              lines when condition/finish differ; a partial find must say how many of each part
              are in hand. Missing keeps the expected snapshot and pays for nothing.
            </p>
          )}
          {detail.items.length > 0 && <ReceiveValueStrip items={detail.items} />}
          {lines.length > 0 && <LotPricingSummary items={lines} />}
          {reviewFlaggedCount > 0 && (
            <p style={styles.reviewBanner} role="status">
              {reviewFlaggedCount} line{reviewFlaggedCount === 1 ? '' : 's'} flagged for pricing
              review (missing markets and/or offer &gt;90% of Mana Pool Market) — use Review flags /
              Sort below to isolate them before intake / channel sync.
            </p>
          )}
          {(canEditLines || lines.length > 0) && (
            <div style={styles.checklistBar}>
              <label style={styles.field}>
                <span style={styles.label}>Card # / name</span>
                <input
                  ref={cardNumberFilterRef}
                  type="search"
                  value={cardNumberFilter}
                  onChange={(e) => setCardNumberFilter(e.target.value)}
                  placeholder="e.g. 161 or Bolt"
                  style={styles.input}
                  autoFocus={Boolean(canEditLines)}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>Review flags</span>
                <select
                  value={reviewFlagFilter}
                  onChange={(e) => setReviewFlagFilter(e.target.value as BuylistReviewFlagFilter)}
                  style={styles.input}
                  aria-label="Filter by review flag"
                >
                  {BUYLIST_REVIEW_FLAG_FILTERS.map((opt) => {
                    const countSuffix =
                      opt.id === 'flagged'
                        ? ` (${reviewFlaggedCount})`
                        : opt.id === 'missing_pricing'
                          ? ` (${missingPricingCount})`
                          : opt.id === 'high_offer'
                            ? ` (${highOfferCount})`
                            : opt.id === 'unflagged'
                              ? ` (${Math.max(0, lines.length - reviewFlaggedCount)})`
                              : ` (${lines.length})`;
                    return (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                        {countSuffix}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label style={styles.field}>
                <span style={styles.label}>Sort</span>
                <select
                  value={reviewLineSort}
                  onChange={(e) => setReviewLineSort(e.target.value as BuylistReviewLineSort)}
                  style={styles.input}
                  aria-label="Sort receive checklist"
                >
                  {BUYLIST_REVIEW_LINE_SORTS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={styles.checkFilterLabel}>
                <input
                  type="checkbox"
                  checked={uncheckedOnly}
                  onChange={(e) => setUncheckedOnly(e.target.checked)}
                />
                Unchecked only
              </label>
              <span style={styles.checklistProgress}>
                {copyProgress.received} of {copyProgress.expected} copies
                {filteredLines.length !== lines.length
                  ? ` · showing ${filteredLines.length}`
                  : ''}
              </span>
            </div>
          )}
          <ItemTable
            items={filteredLines}
            editable={Boolean(canEditLines)}
            rematchItemId={rematchItemId}
            splitItemId={splitItemId}
            onRematchToggle={(itemId) => {
              setRematchItemId((cur) => (cur === itemId ? null : itemId));
              setSplitItemId(null);
              setMatchItemId(null);
            }}
            onSplitToggle={(itemId) => {
              setSplitItemId((cur) => (cur === itemId ? null : itemId));
              setRematchItemId(null);
              setMatchItemId(null);
            }}
            onToggleChecked={(itemId, checked) => toggleLineChecked(itemId, checked)}
            onAdjustReceived={(itemId, delta) => adjustReceived(itemId, delta)}
            onCorrectExpected={(itemId, expected) => correctExpected(itemId, expected)}
            onSave={(itemId, patch) => saveItemUpdates([{ item_id: itemId, ...patch }])}
            onReject={(itemId) =>
              saveItemUpdates([{ item_id: itemId, status: 'rejected_on_grading' }], 'Line rejected.')
            }
            onApprove={(itemId) =>
              saveItemUpdates([{ item_id: itemId, status: 'accepted_on_grading' }], 'Line approved.')
            }
            onMissing={(itemId) =>
              saveItemUpdates(
                [
                  {
                    item_id: itemId,
                    quantity: 0,
                    included: false,
                    status: 'rejected_on_grading',
                    fail_reason: 'Missing on arrival',
                    line_checked: true,
                  },
                ],
                'Marked missing.',
              )
            }
            onReplace={(itemId, product, details) => {
              void run(
                () => replaceBuylistItem(id, itemId, buylistCatalogLinePayload(product, details)),
                'Wrong card superseded — corrected line added for offer/intake.',
              );
              setRematchItemId(null);
            }}
            onSplit={(itemId, parts) => {
              void run(
                () => splitBuylistItem(id, itemId, { parts }),
                'Line split — each part keeps its own condition/finish for offer/intake.',
              );
              setSplitItemId(null);
            }}
          />

          <ReceiveChangeList events={detail?.changeEvents ?? []} />

          <h2 style={{ ...typography.h2, marginTop: spacing.lg }}>Rejected / unmatched / superseded</h2>
          {canEditLines && rejected.some((i) => i.status === 'unmatched' || i.status === 'rejected_on_grading') && (
            <p style={styles.hint}>
              Unmatched: use Match (same Add Inventory product search) to attach the correct card and
              price the line for offer/COGS/intake, or Remove to drop it from the submission. Superseded
              lines stay for audit.
            </p>
          )}
          <ItemTable
            items={rejected}
            editable={false}
            unmatchedActions={Boolean(canEditLines)}
            matchItemId={matchItemId}
            onMatchToggle={(itemId) => {
              setMatchItemId((cur) => (cur === itemId ? null : itemId));
              setRematchItemId(null);
              setSplitItemId(null);
            }}
            onMatch={(itemId, product, details) => {
              void run(
                () => matchBuylistItem(id, itemId, buylistCatalogLinePayload(product, details)),
                'Line matched — priced for offer/intake.',
              );
              setMatchItemId(null);
            }}
            onRemove={(itemId) => {
              void run(() => removeBuylistItem(id, itemId), 'Line removed from submission.');
              setMatchItemId(null);
            }}
          />
        </>
      )}
    </div>
  );
};

const Meta: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={styles.metaLabel}>{label}</div>
    <div style={styles.metaValue}>{value}</div>
  </div>
);

type ItemPatch = Omit<BuylistItemUpdate, 'item_id'>;

type ReplaceDetails = { condition: string; finish: string; quantity: number };

type SplitPart = {
  quantity: number;
  received_quantity?: number;
  condition: string;
  finish: string;
};

const ItemTable: React.FC<{
  items: BuylistSubmissionItem[];
  editable?: boolean;
  unmatchedActions?: boolean;
  rematchItemId?: string | null;
  matchItemId?: string | null;
  splitItemId?: string | null;
  onRematchToggle?: (itemId: string) => void;
  onMatchToggle?: (itemId: string) => void;
  onSplitToggle?: (itemId: string) => void;
  onToggleChecked?: (itemId: string, checked: boolean) => void;
  onAdjustReceived?: (itemId: string, delta: number) => void;
  onCorrectExpected?: (itemId: string, expected: number) => void;
  onSave?: (itemId: string, patch: ItemPatch) => void;
  onReject?: (itemId: string) => void;
  onApprove?: (itemId: string) => void;
  onMissing?: (itemId: string) => void;
  onReplace?: (itemId: string, product: Product, details: ReplaceDetails) => void;
  onMatch?: (itemId: string, product: Product, details: ReplaceDetails) => void;
  onRemove?: (itemId: string) => void;
  onSplit?: (itemId: string, parts: SplitPart[]) => void;
}> = ({
  items,
  editable,
  unmatchedActions,
  rematchItemId,
  matchItemId,
  splitItemId,
  onRematchToggle,
  onMatchToggle,
  onSplitToggle,
  onToggleChecked,
  onAdjustReceived,
  onCorrectExpected,
  onSave,
  onReject,
  onApprove,
  onMissing,
  onReplace,
  onMatch,
  onRemove,
  onSplit,
}) => {
  const showActions = Boolean(editable || unmatchedActions);
  return (
  <div style={styles.tableWrap}>
    <table style={styles.table}>
      <thead>
        <tr>
          {editable && <th style={styles.th}>✓</th>}
          <th style={styles.th}>#</th>
          <th style={styles.th}>Card</th>
          <th style={styles.th}>Set</th>
          <th style={styles.th}>Cond</th>
          <th style={styles.th}>Finish</th>
          <th style={styles.th}>Received</th>
          <th style={styles.th}>Markets (offer %)</th>
          <th style={styles.th}>Offer / unit</th>
          <th style={styles.th}>Status</th>
          {showActions && <th style={styles.th}>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <EditableItemRow
            key={item.id}
            item={item}
            editable={Boolean(editable)}
            unmatchedActions={Boolean(unmatchedActions)}
            rematching={rematchItemId === item.id}
            matching={matchItemId === item.id}
            splitting={splitItemId === item.id}
            onRematchToggle={onRematchToggle}
            onMatchToggle={onMatchToggle}
            onSplitToggle={onSplitToggle}
            onToggleChecked={onToggleChecked}
            onAdjustReceived={onAdjustReceived}
            onCorrectExpected={onCorrectExpected}
            onSave={onSave}
            onReject={onReject}
            onApprove={onApprove}
            onMissing={onMissing}
            onReplace={onReplace}
            onMatch={onMatch}
            onRemove={onRemove}
            onSplit={onSplit}
          />
        ))}
        {items.length === 0 && (
          <tr>
            <td
              colSpan={showActions ? (editable ? 11 : 10) : 9}
              style={{ ...styles.td, color: colors.textSecondary }}
            >
              No lines.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
  );
};

const EditableItemRow: React.FC<{
  item: BuylistSubmissionItem;
  editable: boolean;
  unmatchedActions?: boolean;
  rematching: boolean;
  matching?: boolean;
  splitting: boolean;
  onRematchToggle?: (itemId: string) => void;
  onMatchToggle?: (itemId: string) => void;
  onSplitToggle?: (itemId: string) => void;
  onToggleChecked?: (itemId: string, checked: boolean) => void;
  onAdjustReceived?: (itemId: string, delta: number) => void;
  onCorrectExpected?: (itemId: string, expected: number) => void;
  onSave?: (itemId: string, patch: ItemPatch) => void;
  onReject?: (itemId: string) => void;
  onApprove?: (itemId: string) => void;
  onMissing?: (itemId: string) => void;
  onReplace?: (itemId: string, product: Product, details: ReplaceDetails) => void;
  onMatch?: (itemId: string, product: Product, details: ReplaceDetails) => void;
  onRemove?: (itemId: string) => void;
  onSplit?: (itemId: string, parts: SplitPart[]) => void;
}> = ({
  item,
  editable,
  unmatchedActions,
  rematching,
  matching,
  splitting,
  onRematchToggle,
  onMatchToggle,
  onSplitToggle,
  onToggleChecked,
  onAdjustReceived,
  onCorrectExpected,
  onSave,
  onReject,
  onApprove,
  onMissing,
  onReplace,
  onMatch,
  onRemove,
  onSplit,
}) => {
  const expectedNow = lineExpectedQuantity(item);
  const receivedNow = lineReceivedQuantity(item);
  const [qty, setQty] = useState(String(expectedNow || item.quantity || 1));
  const [expectedDraft, setExpectedDraft] = useState(String(expectedNow));
  const serverExpectedRef = useRef(expectedNow);
  const [condition, setCondition] = useState(item.gradedCondition || item.condition || 'NM');
  const finishOptions = finishesForLine(item);
  const catalogAutoFinish = uniqueFoilFamilyFinish(finishOptions);
  const [finish, setFinish] = useState(() => {
    const current = item.finish || 'normal';
    if (catalogAutoFinish && (current === 'foil' || current === 'etched')) {
      return catalogAutoFinish;
    }
    return current;
  });
  const [offerUnit, setOfferUnit] = useState(
    String(lineOfferUnit(item) ?? ''),
  );
  const [splitOffQty, setSplitOffQty] = useState('1');
  const [splitReceivedQty, setSplitReceivedQty] = useState('0');
  const [splitCondition, setSplitCondition] = useState('LP');
  const [splitFinish, setSplitFinish] = useState(item.finish || 'normal');
  const checked = lineFullyReceived(item);
  const canMatchRemove =
    Boolean(unmatchedActions) &&
    (item.status === 'unmatched' || item.status === 'rejected_on_grading');
  const finishBlocked = lineFinishBlocked(item);
  const needsFinishPick = lineNeedsFinishPick(item);
  const catalogHint =
    item.finishResolveSource === 'auto_sku' ||
    (catalogAutoFinish != null && finish === catalogAutoFinish)
      ? `Finish set from catalog (${finish})`
      : null;

  useEffect(() => {
    const expected = lineExpectedQuantity(item);
    setQty(String(expected || item.quantity || 1));
    // Only follow the server snapshot. A mount or unrelated field refresh must
    // not wipe an in-progress Expected edit (that left Correct disabled).
    if (expected !== serverExpectedRef.current) {
      serverExpectedRef.current = expected;
      setExpectedDraft(String(expected));
    }
    setCondition(item.gradedCondition || item.condition || 'NM');
    const options = finishesForLine(item);
    const unique = uniqueFoilFamilyFinish(options);
    const current = item.finish || 'normal';
    if (unique && (current === 'foil' || current === 'etched' || !options.includes(current))) {
      setFinish(unique);
    } else {
      setFinish(current);
    }
    setOfferUnit(String(lineOfferUnit(item) ?? ''));
  }, [
    item.quantity,
    item.expectedQuantity,
    item.receivedQuantity,
    item.gradedCondition,
    item.condition,
    item.finish,
    item.gradedUnitOffer,
    item.quotedUnitOffer,
    item.availableFinishes,
    item.finishResolveSource,
  ]);

  useEffect(() => {
    if (splitting) {
      setSplitOffQty('1');
      setSplitCondition(
        (item.gradedCondition || item.condition || 'NM') === 'NM' ? 'LP' : 'NM',
      );
      setSplitFinish(item.finish || 'normal');
    }
  }, [splitting, item.gradedCondition, item.condition, item.finish]);

  const matchDetails: ReplaceDetails = {
    condition,
    finish,
    quantity: Math.max(1, Number(qty) || expectedNow || 1),
  };

  if (!editable) {
    return (
      <>
        <tr style={item.status === 'superseded' ? styles.supersededRow : undefined}>
          <td style={styles.td}>{item.cardNumber || '—'}</td>
          <td style={styles.td}>
            {item.cardName}
            {item.replacesItemId ? ' · correction' : ''}
          </td>
          <td style={styles.td}>{item.setCode}</td>
          <td style={styles.td}>
            {canMatchRemove ? (
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                style={styles.inputCompact}
              >
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : (
              item.gradedCondition || item.condition
            )}
          </td>
          <td style={styles.td}>
            {canMatchRemove ? (
              <select
                value={finish}
                onChange={(e) => setFinish(e.target.value)}
                style={styles.inputCompact}
              >
                {finishOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            ) : (
              item.finish
            )}
            {catalogHint ? <div style={styles.hintInline}>{catalogHint}</div> : null}
            {needsFinishPick ? (
              <div style={styles.failReason}>
                {item.finishBlockMessage || FINISH_BLOCK_HINT}
              </div>
            ) : null}
          </td>
          <td style={styles.td}>
            {canMatchRemove ? (
              <input
                type="number"
                min={1}
                step={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                style={{ ...styles.inputCompact, width: 64 }}
              />
            ) : (
              `${receivedNow} of ${expectedNow}`
            )}
          </td>
          <td style={styles.td}>
            <MarketSignalsCell item={item} />
          </td>
          <td style={styles.td}>
            <div>{formatMoney(lineOfferUnit(item))}</div>
            <div style={styles.hintInline}>line {formatMoney(linePayableTotal(item))}</div>
          </td>
          <td style={styles.td}>
            {item.status.replace(/_/g, ' ')}
            {item.failReason ? (
              <div style={styles.failReason}>{item.failReason}</div>
            ) : null}
          </td>
          {unmatchedActions && (
            <td style={styles.td}>
              {canMatchRemove ? (
                <div style={styles.rowActions}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onMatchToggle?.(item.id)}
                  >
                    {matching ? 'Cancel' : 'Match'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove?.(item.id)}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                '—'
              )}
            </td>
          )}
        </tr>
        {matching && canMatchRemove && (
          <tr>
            <td colSpan={10} style={{ ...styles.td, background: colors.gray50 }}>
              <p style={styles.hint}>
                Same product search as Add Inventory. Selecting a card matches this line in place and
                prices it for offer / COGS / intake ({condition} · {finish} · qty{' '}
                {matchDetails.quantity}).
              </p>
              <ProductSearchAutocomplete
                onProductSelect={(product) => onMatch?.(item.id, product, matchDetails)}
                defaultCondition={condition}
              />
            </td>
          </tr>
        )}
      </>
    );
  }

  const currentOffer = lineOfferUnit(item);
  const parsedOffer = offerUnit.trim() === '' ? null : Number(offerUnit);
  const offerEdited =
    parsedOffer != null &&
    Number.isFinite(parsedOffer) &&
    (currentOffer == null || Math.abs(parsedOffer - currentOffer) > 0.001);
  const dirty =
    condition !== (item.gradedCondition || item.condition) ||
    finish !== item.finish ||
    offerEdited;

  const expectedDirty =
    Number.isFinite(Number(expectedDraft)) && Number(expectedDraft) !== expectedNow;

  const replaceDetails: ReplaceDetails = {
    condition,
    finish,
    quantity: Math.max(1, Number(expectedDraft) || expectedNow || 1),
  };

  const canSplit = expectedNow >= 2;
  const partialReceive = receivedNow > 0 && receivedNow < expectedNow;
  const peelQty = Math.max(1, Math.min(expectedNow - 1, Number(splitOffQty) || 1));
  const remainQty = expectedNow - peelQty;
  const peelReceived = partialReceive
    ? Math.max(0, Math.min(peelQty, Number(splitReceivedQty) || 0))
    : receivedNow === 0
      ? 0
      : peelQty;
  const remainReceived = receivedNow - peelReceived;
  const splitPartsValid =
    remainQty >= 1 &&
    peelQty >= 1 &&
    remainQty + peelQty === expectedNow &&
    remainReceived >= 0 &&
    peelReceived >= 0 &&
    remainReceived <= remainQty &&
    peelReceived <= peelQty &&
    remainReceived + peelReceived === receivedNow;

  return (
    <>
      <tr style={checked ? styles.checkedRow : undefined}>
        <td style={styles.td}>
          <input
            type="checkbox"
            checked={checked}
            aria-label={`Mark one ${item.cardName} received`}
            onChange={(e) => onToggleChecked?.(item.id, e.target.checked)}
          />
        </td>
        <td style={{ ...styles.td, fontWeight: 600 }}>{item.cardNumber || '—'}</td>
        <td style={styles.td}>
          {item.cardName}
          {item.replacesItemId ? (
            <span style={styles.correctionBadge}> correction</span>
          ) : null}
        </td>
        <td style={styles.td}>{item.setCode}</td>
        <td style={styles.td}>
          <select value={condition} onChange={(e) => setCondition(e.target.value)} style={styles.inputCompact}>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </td>
        <td style={styles.td}>
          <select value={finish} onChange={(e) => setFinish(e.target.value)} style={styles.inputCompact}>
            {finishOptions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          {catalogHint ? <div style={styles.hintInline}>{catalogHint}</div> : null}
          {needsFinishPick ? (
            <div style={styles.failReason}>
              {item.finishBlockMessage || FINISH_BLOCK_HINT}
            </div>
          ) : null}
        </td>
        <td style={styles.td}>
          <div style={styles.qtyStepper}>
            <button
              type="button"
              style={styles.stepBtn}
              aria-label={`Mark one less ${item.cardName} received`}
              disabled={receivedNow <= 0}
              onClick={() => onAdjustReceived?.(item.id, -1)}
            >
              −
            </button>
            <span style={styles.qtyLabel} aria-label={`${receivedNow} of ${expectedNow} received`}>
              {receivedNow} of {expectedNow}
            </span>
            <button
              type="button"
              style={styles.stepBtn}
              aria-label={`Mark one ${item.cardName} received`}
              disabled={receivedNow >= expectedNow}
              onClick={() => onAdjustReceived?.(item.id, 1)}
            >
              +
            </button>
          </div>
          <div style={styles.expectedCorrect}>
            <label style={styles.expectedLabel}>
              Expected
              <input
                type="number"
                min={receivedNow}
                step={1}
                value={expectedDraft}
                onChange={(e) => setExpectedDraft(e.target.value)}
                style={{ ...styles.inputCompact, width: 56 }}
                aria-label={`Expected quantity for ${item.cardName}`}
              />
            </label>
            <button
              type="button"
              style={styles.stepBtn}
              disabled={!expectedDirty}
              onClick={() => onCorrectExpected?.(item.id, Number(expectedDraft))}
            >
              Correct
            </button>
          </div>
        </td>
        <td style={styles.td}>
          <MarketSignalsCell
            item={item}
            offerOverride={
              parsedOffer != null && Number.isFinite(parsedOffer)
                ? parsedOffer
                : lineOfferUnit(item)
            }
          />
        </td>
        <td style={styles.td}>
          <input
            type="number"
            min={0}
            step={0.01}
            value={offerUnit}
            onChange={(e) => setOfferUnit(e.target.value)}
            style={{ ...styles.inputCompact, width: 88 }}
            aria-label={`Offer unit for ${item.cardName}`}
          />
          <div style={styles.hintInline}>
            line {formatMoney(
              parsedOffer != null && Number.isFinite(parsedOffer)
                ? parsedOffer * receivedNow
                : linePayableTotal(item),
            )}
          </div>
        </td>
        <td style={styles.td}>{item.status.replace(/_/g, ' ')}</td>
        <td style={styles.td}>
          <div style={styles.rowActions}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!dirty || (finishBlocked && !finishOptions.includes(finish))}
              title={
                finishBlocked && !finishOptions.includes(finish)
                  ? item.finishBlockMessage || FINISH_BLOCK_HINT
                  : undefined
              }
              onClick={() =>
                onSave?.(item.id, {
                  graded_condition: condition,
                  finish,
                  // Only an edited offer is explicit. An untouched field must not
                  // block live market + CK floor reprice on condition/finish.
                  // Save does not step received — use the checklist / stepper.
                  ...(offerEdited ? { graded_unit_offer: parsedOffer as number } : {}),
                })
              }
            >
              Save
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onApprove?.(item.id)}>
              Approve
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onReject?.(item.id)}>
              Reject
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onMissing?.(item.id)}>
              Missing
            </Button>
            {canSplit && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onSplitToggle?.(item.id)}>
                {splitting ? 'Cancel split' : 'Split'}
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => onRematchToggle?.(item.id)}>
              {rematching ? 'Cancel' : 'Wrong card'}
            </Button>
          </div>
        </td>
      </tr>
      {splitting && canSplit && (
        <tr>
          <td colSpan={11} style={{ ...styles.td, background: colors.gray50 }}>
            <p style={styles.hint}>
              Peel expected copies into a sibling with a different condition and/or finish. Parts
              must sum to expected {expectedNow}
              {partialReceive ? ` and state how many of the ${receivedNow} in hand move` : ''}.
              This does not shrink the declared total. Offer, COGS, and intake follow copies in hand.
            </p>
            <div style={{ ...styles.inlineRow, marginBottom: spacing.sm }}>
              <label style={styles.field}>
                <span style={styles.label}>Peel expected</span>
                <input
                  type="number"
                  min={1}
                  max={expectedNow - 1}
                  step={1}
                  value={splitOffQty}
                  onChange={(e) => setSplitOffQty(e.target.value)}
                  style={{ ...styles.inputCompact, width: 72 }}
                />
              </label>
              {partialReceive && (
                <label style={styles.field}>
                  <span style={styles.label}>In hand on new line</span>
                  <input
                    type="number"
                    min={0}
                    max={Math.min(peelQty, receivedNow)}
                    step={1}
                    value={splitReceivedQty}
                    onChange={(e) => setSplitReceivedQty(e.target.value)}
                    style={{ ...styles.inputCompact, width: 72 }}
                    aria-label={`Received copies moving to the new ${item.cardName} line`}
                  />
                </label>
              )}
              <label style={styles.field}>
                <span style={styles.label}>New line cond</span>
                <select
                  value={splitCondition}
                  onChange={(e) => setSplitCondition(e.target.value)}
                  style={styles.inputCompact}
                >
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label style={styles.field}>
                <span style={styles.label}>New line finish</span>
                <select
                  value={splitFinish}
                  onChange={(e) => setSplitFinish(e.target.value)}
                  style={styles.inputCompact}
                >
                  {finishOptions.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!splitPartsValid}
                onClick={() =>
                  onSplit?.(item.id, [
                    {
                      quantity: remainQty,
                      received_quantity: remainReceived,
                      condition,
                      finish,
                    },
                    {
                      quantity: peelQty,
                      received_quantity: peelReceived,
                      condition: splitCondition,
                      finish: splitFinish,
                    },
                  ])
                }
              >
                Confirm split
              </Button>
            </div>
            <span style={styles.hint}>
              Result: {remainReceived} of {remainQty} {condition} {finish} (this line) + {peelReceived}{' '}
              of {peelQty} {splitCondition} {splitFinish} (new). Expected total stays {expectedNow}.
            </span>
          </td>
        </tr>
      )}
      {rematching && (
        <tr>
          <td colSpan={11} style={{ ...styles.td, background: colors.gray50 }}>
            <p style={styles.hint}>
              Search the catalog for the card in hand (same Add Inventory product search). The qty
              below is how many copies this correction applies to (expected and in hand on the new
              line). Confirming supersedes this submission line for offer / COGS / intake and keeps
              its expected snapshot on the superseded row. Split first if only some copies are wrong.
            </p>
            <div style={{ ...styles.inlineRow, marginBottom: spacing.sm }}>
              <span style={styles.hint}>
                Will add: {condition} · {finish} · qty {replaceDetails.quantity} (set Expected above)
              </span>
            </div>
            <ProductSearchAutocomplete
              onProductSelect={(product) => onReplace?.(item.id, product, replaceDetails)}
              defaultCondition={condition}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const ReceiveChangeList: React.FC<{ events: BuylistChangeEvent[] }> = ({ events }) => (
  <section aria-label="Receive change list" style={styles.changeList}>
    <h2 style={{ ...typography.h2, marginTop: spacing.lg }}>Change list</h2>
    <p style={styles.hint}>
      Append-only log of Condition, Finish, Qty, and Rejected / unmatched / superseded edits.
      Saved with the submission, so it is still here after a refresh. Oldest first.
    </p>
    {events.length === 0 ? (
      <p style={styles.hint}>No receive changes yet.</p>
    ) : (
      <ol style={styles.changeItems}>
        {events.map((event) => (
          <li key={event.id} style={styles.changeItem} data-line-id={event.itemId ?? ''}>
            <span>{formatInstant(event.createdAt)}</span>
            {' · '}
            <strong>{changeEventCardLabel(event)}</strong>
            {' · '}
            {buylistChangeKindLabel(event.kind)}: {event.before || '—'} → {event.after || '—'}
            {' · '}
            <span>{formatBuylistOfferDelta(event.offerDelta)}</span>
          </li>
        ))}
      </ol>
    )}
  </section>
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: spacing.lg,
    maxWidth: 1280,
  },
  backLink: {
    color: colors.primary,
    textDecoration: 'none',
    ...typography.small,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    background: colors.surface,
    marginBottom: spacing.lg,
  },
  hint: {
    ...typography.small,
    color: colors.textSecondary,
    margin: `0 0 ${spacing.sm}`,
  },
  hintInline: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  marketStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 168,
  },
  marketRow: {
    display: 'grid',
    gridTemplateColumns: '104px 1fr auto',
    gap: spacing.xs,
    alignItems: 'baseline',
    ...typography.caption,
  },
  marketLabel: {
    color: colors.textSecondary,
  },
  marketValue: {
    fontVariantNumeric: 'tabular-nums',
  },
  marketPct: {
    color: colors.textSecondary,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right' as const,
    minWidth: 36,
  },
  marketPctHot: {
    color: colorExtensions.warningDark,
    fontWeight: 700,
  },
  flagStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    marginTop: spacing.xs,
  },
  flagMissing: {
    ...typography.caption,
    color: colorExtensions.errorDark,
    fontWeight: 600,
  },
  flagHigh: {
    ...typography.caption,
    color: colorExtensions.warningDark,
    fontWeight: 600,
  },
  lotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: spacing.sm,
  },
  lotCell: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    background: colors.gray50,
  },
  reviewBanner: {
    ...typography.small,
    color: colorExtensions.warningDark,
    background: '#fff8e1',
    border: `1px solid ${colors.warning}`,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    marginBottom: spacing.md,
    fontWeight: 600,
  },
  warn: {
    ...typography.small,
    color: colors.warning,
    margin: `0 0 ${spacing.sm}`,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.surface,
  },
  metaLabel: {
    ...typography.small,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  metaValue: {
    ...typography.body,
    fontWeight: 600,
    textTransform: 'capitalize' as const,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.sm,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  label: {
    ...typography.small,
    color: colors.textSecondary,
  },
  input: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    font: 'inherit',
    background: colors.backgroundAlt,
  },
  qtyStepper: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  qtyLabel: {
    ...typography.small,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    minWidth: 52,
    textAlign: 'center' as const,
  },
  stepBtn: {
    ...typography.small,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    borderRadius: borderRadius.sm,
    minWidth: 28,
    height: 28,
    cursor: 'pointer',
    color: colors.text,
  },
  expectedCorrect: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  expectedLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    ...typography.caption,
    color: colors.textSecondary,
  },
  inputCompact: {
    padding: '4px 6px',
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    font: 'inherit',
    background: colors.backgroundAlt,
    maxWidth: 100,
  },
  inlineRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'flex-end',
  },
  quoteBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    background: colors.gray50,
    minWidth: 240,
  },
  rateOptionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  rateOptionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  rateOptionGroupTitle: {
    ...typography.small,
    fontWeight: 700,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  rateOptionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    background: colors.surface,
    cursor: 'pointer',
  },
  rateOptionRowSelected: {
    borderColor: colors.primary,
    background: colors.backgroundAlt,
  },
  rateOptionMain: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  rateOptionTotal: {
    ...typography.body,
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
  },
  rateMessages: {
    margin: `${spacing.xs} 0 0`,
    paddingLeft: spacing.md,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  stepList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    ...typography.body,
    color: colors.textSecondary,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
  },
  stepCurrent: {
    background: colors.gray50,
    color: colors.text,
    fontWeight: 600,
  },
  stepDone: {
    color: colors.textSecondary,
  },
  stepNum: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: borderRadius.sm,
    background: colors.gray100,
    ...typography.small,
    fontWeight: 600,
  },
  stepBadge: {
    ...typography.caption,
    color: colors.primary,
    marginLeft: 'auto',
  },
  stepBadgeMuted: {
    ...typography.caption,
    color: colors.textSecondary,
    marginLeft: 'auto',
  },
  advancedToggle: {
    marginTop: spacing.md,
    background: 'none',
    border: 'none',
    color: colors.primary,
    cursor: 'pointer',
    padding: 0,
    ...typography.small,
    fontWeight: 600,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.surface,
    marginBottom: spacing.md,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: spacing.sm,
    textAlign: 'left',
    background: colors.gray100,
    ...typography.small,
    color: colors.textSecondary,
    fontWeight: 600,
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: spacing.sm,
    borderTop: `1px solid ${colors.border}`,
    ...typography.body,
    verticalAlign: 'top',
  },
  rowActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  checklistBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'flex-end',
    marginBottom: spacing.sm,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.surface,
  },
  checkFilterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    ...typography.small,
    color: colors.textSecondary,
    paddingBottom: spacing.sm,
  },
  changeList: {
    marginTop: spacing.md,
  },
  changeItems: {
    listStyle: 'decimal',
    margin: 0,
    paddingLeft: spacing.lg,
    ...typography.small,
  },
  changeItem: {
    padding: `${spacing.xs} 0`,
    borderBottom: `1px solid ${colors.border}`,
  },
  checklistProgress: {
    ...typography.small,
    color: colors.textSecondary,
    fontWeight: 600,
    paddingBottom: spacing.sm,
    marginLeft: 'auto',
  },
  checkedRow: {
    background: colors.gray50,
  },
  supersededRow: {
    opacity: 0.75,
  },
  failReason: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  correctionBadge: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: 600,
  },
  labelQrImg: {
    maxWidth: 220,
    width: '100%',
    height: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.surface,
    padding: spacing.sm,
  },
  link: {
    color: colors.primary,
    fontWeight: 600,
  },
  error: { color: colors.error },
  success: { color: colors.success },
};

export default BuylistSubmissionPage;
